import { useState } from 'react';
import { Flame, CheckCircle2, Loader2 } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
  Button,
  Input,
  Textarea,
  SegmentedControl,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { usePurge, useWarm, usePurgeStatus, isPurgeComplete } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';

const MODES = [
  { value: 'url', label: 'By URL' },
  { value: 'prefix', label: 'By prefix' },
  { value: 'suffix', label: 'By suffix' },
  { value: 'tag', label: 'By tag' },
  { value: 'all', label: 'Purge all' },
];

export function PurgeWarmTab({ domain, domainId }: { domain: string; domainId: string }) {
  return (
    <Section title="Purge & Warm" description={`Invalidate or pre-warm cached content for ${domain}.`}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <PurgeCard domainId={domainId} />
        <WarmCard domainId={domainId} />
      </div>
    </Section>
  );
}

function PurgeCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const [mode, setMode] = useState('url');
  const [text, setText] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [purgeId, setPurgeId] = useState<string | null>(null);

  const purge = usePurge(domainId, {
    onSuccess: (res) => {
      trackFeatureAction('cdn', 'purge', 'create');
      show({ tone: 'success', title: 'Purge dispatched' });
      setPurgeId(res?.purge_id ?? null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Purge failed', description: errorMessage(e, 'Purge failed') }),
  });
  const { data: status } = usePurgeStatus(domainId, purgeId);
  const complete = isPurgeComplete(status);

  const destructive = mode === 'all';
  const canSubmit = destructive ? confirmAll : text.trim().length > 0;

  const submit = () => {
    const values =
      mode === 'all' ? [] : mode === 'prefix' || mode === 'suffix' ? [text.trim()] : text.split('\n').map((s) => s.trim()).filter(Boolean);
    purge.mutate({ mode, values });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Purge cache</CardTitle>
        <CardDescription>Remove cached content from every edge node.</CardDescription>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <SegmentedControl items={MODES} value={mode} onValueChange={(v) => { setMode(v); setText(''); setConfirmAll(false); }} aria-label="Purge mode" />

        {mode === 'all' ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-lg)', cursor: 'pointer' }}>
            <Checkbox checked={confirmAll} onCheckedChange={(v) => setConfirmAll(!!v)} />
            I understand this purges <b>all</b> cached content for this domain.
          </label>
        ) : mode === 'prefix' || mode === 'suffix' ? (
          <Field label={mode === 'prefix' ? 'Path prefix' : 'Path suffix'}>
            {(p) => <Input {...p} value={text} placeholder={mode === 'prefix' ? '/assets/' : '.jpg'} onChange={(e) => setText(e.target.value)} />}
          </Field>
        ) : (
          <Field label={mode === 'url' ? 'URLs' : 'Tags'} description="One per line.">
            {(p) => <Textarea {...p} rows={4} value={text} placeholder={mode === 'url' ? 'https://example.com/page' : 'homepage'} onChange={(e) => setText(e.target.value)} style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }} />}
          </Field>
        )}

        {purgeId && status && (
          <Alert tone={complete ? 'success' : 'info'} icon={complete ? <CheckCircle2 size={16} /> : <Loader2 size={16} />}>
            Propagation: {status.acks?.length ?? 0}/{status.total_nodes} nodes
            {complete ? ' — complete.' : ' — in progress…'}
          </Alert>
        )}
      </CardBody>
      <CardFooter style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant={destructive ? 'danger' : 'primary'} disabled={!canSubmit || purge.isPending} onClick={submit}>
          {purge.isPending ? 'Purging…' : destructive ? 'Purge everything' : 'Purge cache'}
        </Button>
      </CardFooter>
    </Card>
  );
}

function WarmCard({ domainId }: { domainId: string }) {
  const { show } = useToast();
  const [urls, setUrls] = useState('');
  const warm = useWarm(domainId, {
    onSuccess: (res) => {
      trackFeatureAction('cdn', 'purge', 'create');
      show({ tone: 'success', title: `Warm sent${res?.node_count ? ` to ${res.node_count} node(s)` : ''}` });
    },
    onError: (e) => show({ tone: 'danger', title: 'Warm failed', description: errorMessage(e, 'Warm failed') }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Warm cache</CardTitle>
        <CardDescription>Pre-fetch URLs so the first visitor gets a cache hit.</CardDescription>
      </CardHeader>
      <CardBody>
        <Field label="URLs to warm" description="One per line.">
          {(p) => <Textarea {...p} rows={3} value={urls} placeholder="https://example.com/page" onChange={(e) => setUrls(e.target.value)} style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }} />}
        </Field>
      </CardBody>
      <CardFooter style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="secondary" startIcon={<Flame size={15} />} disabled={!urls.trim() || warm.isPending} onClick={() => warm.mutate(urls.split('\n').map((s) => s.trim()).filter(Boolean))}>
          {warm.isPending ? 'Warming…' : 'Warm cache'}
        </Button>
      </CardFooter>
    </Card>
  );
}
